import { Haptics, ImpactStyle } from "@capacitor/haptics";
import { IonButton, IonContent, IonPage, IonRefresher, IonRefresherContent, RefresherEventDetail, } from "@ionic/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format, parseISO } from 'date-fns';
import { arrowBack, } from "ionicons/icons";
import React, { useContext, useEffect, useState } from "react";
import { RouteComponentProps, useHistory, useLocation } from "react-router";
import { Events, Params, Screens, useAnalytics } from '../AppAnalyticsProvider';
import { useAppState } from "../AppListenerProvider";
import { AppContext } from "../AppStateProvider";
import { useAuth } from "../AuthProvider";
import BiskHome from "../components/ComponentsBisk/BiskHome/BiskHome";
import CommentDisplay from "../components/ComponentsComments/CommentDisplay/CommentDisplay";
import CustomHelmet from "../components/ComponentsFunctional/CustomHelmet/CustomHelmet";
import ReportReason from "../components/ComponentsProfile/ReportReason/ReportReason";
import ShareToStory from "../components/ComponentsShare/ShareToStory/ShareToStory";
import TrickShare from "../components/ComponentsShare/TrickShare/TrickShare";
import IDXButtonIcon from "../components/ComponentsUI/IDXButtonIcon/IDXButtonIcon";
import IDXChip from "../components/ComponentsUI/IDXChip/IDXChip";
import ToolbarSearchBar from "../components/ComponentsUI/ToolbarSearchBar/ToolbarSearchBar";
import TrickCardExtraPopover from "../components/ComponentsUI/TrickCardExtraPopover/TrickCardExtraPopover";
import TrickDownload from "../components/ComponentsUI/TrickDownload/TrickDownload";
import TrickLike from "../components/ComponentsUI/TrickLike/TrickLike";
import TrickMapLocation from "../components/ComponentsUI/TrickMapLocation/TrickMapLocation";
import TrickSaveComponent from "../components/ComponentsUI/TrickSaveComponent/TrickSaveComponent";
import TrickVideoLoading from "../components/ComponentsUI/TrickVideoLoading/TrickVideoLoading";
import UsernameDisplay from "../components/ComponentsUI/Username/UsernameDisplay";
import VideoTagDisplay from "../components/ComponentsUI/VideoTagDisplay/VideoTagDisplay";
import HomePageVideoPlayer from "../components/ComponentsVideo/HomePageVideo/HomePageVideo";
import { getAdjustedTiming, QueryTiming } from "../hooks/useQueryConfig";
import { useRateApp } from "../hooks/useRateApp";
import { useRevenueCatPaywall } from "../hooks/useRevenueCatPaywall";
import { openNativeMapWithLocation, resolveLocationCoordinates, useTrickLocation } from '../hooks/useTrickLocation';
import { LoadSuggestedTricksStatus } from "../models/loadSuggestedTricksStatus";
import { UserType } from "../models/usertype";
import PreferencesService from "../services/preferences.service";
import TricksService from "../services/tricks.service";
import UserService from "../services/user.service";
import { handleTagClickUtil, splitString, } from "../services/utils";


interface TrickVideoParams {
    user_id?: string;
    id?: string;
}

interface TrickVideoProps extends RouteComponentProps<TrickVideoParams> {
}

const TrickVideo: React.FC<TrickVideoProps> = ({ match }) => {
    // Must see if a user is logged in to see if they can like a trick, post a comment, save a trick, etc.
    // use state to get user profile information
    const { user_id, id } = match.params;

    const { state } = useContext(AppContext);
    const { isLoading: isAuthLoading } = useAuth();
    const { isNative } = useAppState();
    const history = useHistory();
    const location = useLocation();
    const queryClient = useQueryClient();
    const { trackEvent, trackScreen } = useAnalytics();

    const [editTrick, setEditTrick] = useState(false);
    const [isCached, setIsCached] = useState(false);
    const [userType, setUserType] = useState(UserType.UNKNOWN);
    const [formattedDate, setFormattedDate] = useState('');
    const [trickCommentsCount, setTrickCommentsCount] = useState(0);
    const [isAuthed, setIsAuthed] = useState<boolean | undefined>(undefined);
    const [reportOpen, setReportOpen] = useState(false);
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [subscriptionLevel, setSubscriptionLevel] = useState('');
    // While the Story Studio is open over this page, mute the background clip so
    // it doesn't keep playing audio behind the editor (the editor's own preview
    // is muted). UploadSuccess gets this for free — its preview clip is always
    // muted — but here the player honours the user's volume, so we override it.
    const [storyStudioOpen, setStoryStudioOpen] = useState(false);

    const { data: trick, isLoading: isTrickLoading, refetch: refetchUserTrick } = useQuery({
        queryKey: ['userTrick', id, user_id],
        queryFn: () => TricksService.getUserTrick(id, user_id),
        ...getAdjustedTiming(QueryTiming.LOW_FREQUENCY),
    });

    const { data: user } = useQuery({
        queryKey: ['user', user_id],
        queryFn: () => UserService.getUserPublicById(user_id),
        enabled: !!trick,
        ...getAdjustedTiming(QueryTiming.LOW_FREQUENCY),
    });

    const { data: videoProperties } = useQuery({
        queryKey: ['videoProperties', id],
        queryFn: () => TricksService.getTrickVideoProperties(id),
        enabled: !!trick,
        ...getAdjustedTiming(QueryTiming.LOW_FREQUENCY),
    });

    const { data: taggedUsers, refetch: refetchTaggedUsers } = useQuery({
        queryKey: ['taggedUsers', trick?.trick_id],
        queryFn: () => TricksService.getTrickUserTagged(id),
        enabled: !!trick,
        ...getAdjustedTiming(QueryTiming.LOW_FREQUENCY),
    });

    const { data: trickMetadataStats, refetch: refetchTrickMetadataStats } = useQuery({
        queryKey: ['trickMetadataStats', id],
        queryFn: () => TricksService.getTrickInteractionsCount(id),
        enabled: !!trick,
        ...getAdjustedTiming(QueryTiming.MEDIUM_FREQUENCY),
    });

    // React Query hook for trick location data
    const { data: trickLocationData, isLoading: isLoadingLocation, error: locationError } = useTrickLocation(
        id,
        true
    );

    const {
        presentPaywall: presentTowRopePaywall,
    } = useRevenueCatPaywall({
        userId: state.user.id,
        userBisk: state.bisk,
        username: state.user.username
    });

    // Auto-prompt for app rating when user has 2+ tricks and hasn't been prompted before
    useRateApp({
        userId: state.user.id,
        minTrickCount: 1,
        autoPrompt: true,
    });

    const deleteTrickMutation = useMutation({
        mutationFn: () => UserService.deleteTrick(id, user_id),
        onSuccess: async () => {
            await Promise.all([
                // Invalidate suggested tricks preference
                PreferencesService.setLoadSuggestedTricks(LoadSuggestedTricksStatus.ONE),
                // Invalidate user profile query
                queryClient.invalidateQueries({ queryKey: ['userProfile', user_id] }),
                // Invalidate user tricks query
                queryClient.invalidateQueries({ queryKey: ['userTricks', user_id] }),
                // Invalidate user tricks query
                queryClient.invalidateQueries({ queryKey: ['streak', user_id] }),
                // Invalidate any other related queries
                queryClient.invalidateQueries({ queryKey: ['trickMetadataStats', id] }),
                queryClient.invalidateQueries({ queryKey: ['newTricks'] }),
                queryClient.invalidateQueries({ queryKey: ['userStatsRaw', user_id] }),
            ]);
            history.push('/home');
        },
    });

    useEffect(() => {
        setIsSubscribed(state.isSubscribed);
        setSubscriptionLevel(state.subscriptionLevel);
    }, [state.isSubscribed, state.subscriptionLevel]);

    useEffect(() => {
        trackScreen(Screens.TRICK_VIDEO);
    }, []);

    useEffect(() => {
        if (!isTrickLoading && trick && user && videoProperties && trickMetadataStats) {
            console.log("Prerender ready");
            window.prerenderReady = true;
            parseFilmDate(trick.date);
        }
    }, [trick, isTrickLoading, user, videoProperties, trickMetadataStats]);

    useEffect(() => {
        const checkUserAuth = async () => {
            const auth_id = await PreferencesService.getUserUid();
            setIsAuthed(!!auth_id);
            if (auth_id) {
                const userType = await UserService.getUserType(auth_id);
                setUserType(userType);
                if (userType === UserType.USER) {
                    const response = await UserService.getUserByAuthId(auth_id);
                    const isUserGod = (await UserService.getIsUserGod(auth_id)).result;
                    setEditTrick(response?.id === user_id || isUserGod);
                }
            }
        };

        if (!isAuthLoading) {
            checkUserAuth();
        }
    }, [isAuthLoading, user_id]);

    const handleBack = () => {
        location.key ? history.go(-1) : history.push("/home");
    };

    const handleTagClick = async (tag: string) => {
        const newTag = handleTagClickUtil(tag);
        await Haptics.impact({ style: ImpactStyle.Light });
        trackEvent(Events.BUTTON_CLICK, {
            [Params.BUTTON_NAME]: 'tag',
            [Params.TRICK_ID]: id,
            tag: newTag
        });
        history.push('/search/' + newTag);
    };

    const handleUserTagClick = async (username: string) => {
        await Haptics.impact({ style: ImpactStyle.Light });
        trackEvent(Events.BUTTON_CLICK, {
            [Params.BUTTON_NAME]: 'user_tag',
            [Params.TRICK_ID]: id,
            username
        });
        history.push('/profile/' + username);
    };

    const onClickPerson = (username: string) => {
        trackEvent(Events.BUTTON_CLICK, {
            [Params.BUTTON_NAME]: 'profile',
            [Params.TRICK_ID]: id,
            username
        });
        history.push("/profile/" + username);
    };

    const handleTrickLocationClick = async (locationInfo: string) => {
        trackEvent(Events.BUTTON_CLICK, {
            [Params.BUTTON_NAME]: 'location',
            [Params.TRICK_ID]: id,
            location: locationInfo
        });

        // For Capacitor apps, determine coordinates and open native map
        if (isNative) {
            if (isSubscribed) {
                return await openNativeMapWithLocation(locationInfo, trickLocationData, trick.thumbnail);
            } else {
                return await presentTowRopePaywall();
            }
        }

        // For web apps, resolve coordinates and navigate
        const { lat, lng } = await resolveLocationCoordinates(locationInfo, trickLocationData);
        history.push(`/map?lat=${lat}&long=${lng}`);
    };

    const parseFilmDate = (date: string) => {
        setFormattedDate(format(parseISO(date), 'MMM d, yyyy'));
    };

    const addTaggedUserToComments = (comment: string) => {
        const parsedComment = splitString(comment, /(\@[A-Za-z0-9_.-]+)/);
        return (
            <>
                {parsedComment.map((phrase, index) => {
                    if (phrase.at(0) === '@') {
                        return (
                            <span
                                className="text-indigo-300 cursor-pointer"
                                onClick={async () => {
                                    onClickPerson(phrase.substring(1));
                                }}
                                key={index}
                            >
                                {phrase}
                            </span>
                        );
                    } else {
                        return <span key={index}>{phrase}</span>;
                    }
                })}
            </>
        );
    }

    const onRefreshTrick = async (event: CustomEvent<RefresherEventDetail>) => {
        await refetchTaggedUsers();
        await refetchUserTrick();
        await refetchTrickMetadataStats();
        // Invalidate comments query to trigger refetch
        await queryClient.invalidateQueries({ queryKey: ['trickComments', id] });
        await queryClient.invalidateQueries({ queryKey: ['trickCommentCount', id] });
        event.detail.complete();
    };

    useEffect(() => {
        if (trick && user) {
            trackEvent(Events.VIDEO_PLAY, {
                [Params.TRICK_ID]: id,
                [Params.USER_ID]: user_id,
                viewer_id: state?.user?.id,
                trick_name: trick.trick_name,
                is_external: videoProperties?.is_external
            });
        }
    }, [trick, user]);

    useEffect(() => {
        if (trick && videoProperties) {
            trackEvent('video_metadata', {
                [Params.TRICK_ID]: id,
                aspect_ratio: videoProperties.aspect_ratio,
                is_vertical: !videoProperties.is_horizontal,
                is_external: videoProperties.is_external
            });
        }
    }, [trick, videoProperties]);

    useEffect(() => {
        if (trickMetadataStats) {
            trackEvent('video_engagement', {
                [Params.TRICK_ID]: id,
                votes: trickMetadataStats.votes,
                comments: trickCommentsCount
            });
        }
    }, [trickMetadataStats, trickCommentsCount]);

    if (isTrickLoading) {
        // Remove loading tracking - not useful for analysis
        return (
            <IonPage>
                <CustomHelmet
                    title="Ecliptic // View Clip"
                    description="Explore Ecliptic and Follow Skiers and Snowboarders To Create Your Own Feed"
                    url={`https://ecliptic.day/clip/${user_id}/${id}`}
                />
                <ToolbarSearchBar />
                <IonContent>
                    <TrickVideoLoading />
                </IonContent>
            </IonPage>
        );
    }

    return (
        <IonPage className="overflow-x-auto">
            <CustomHelmet
                title={`Ecliptic // ${user?.username} // ${trick?.trick_name}`}
                description={`Watch ${user?.username}'s ${trick?.trick_name}`}
                image={trick?.thumbnail || process.env.REACT_APP_DEFAULT_OG_IMAGE || ""}
                url={`https://ecliptic.day/clip/${user_id}/${id}`}
            />
            <ToolbarSearchBar />
            <IonContent>
                <IonRefresher slot="fixed" onIonRefresh={onRefreshTrick}>
                    <IonRefresherContent />
                </IonRefresher>
                <div className="flex flex-col flex-nowrap gap-2">
                    <div className="flex flex-row justify-between mx-1 -mb-1">
                        <IonButton fill="default" size="small" onClick={handleBack}>
                            <IDXButtonIcon size="text-xl" icon={arrowBack} />
                        </IonButton>
                        <div>
                            <ReportReason
                                isOpenParent={reportOpen}
                                text={`User ID: ${state.user?.id} wishes to report ${trick?.user_id} with trick id ${trick?.trick_id}`}
                                usernameToReport={user?.username}
                                reportingUsername={state.user?.id}
                                objectIdToReport={trick?.trick_id}
                                reportingId={state.user?.id}
                                reportedId={trick?.user_id}
                                handleIsOpen={setReportOpen}
                            />
                            < TrickCardExtraPopover
                                isCapacitor={isNative}
                                trick={trick}
                                isAuthed={isAuthed}
                                userId={state.user?.auth_id}
                                publicId={state.user?.id}
                                trickId={trick?.trick_id}
                                userType={userType}
                                displayTrickShare={false}
                                displayTrickInstagramShare={true}
                                displayTrickDownload={false}
                                userOnDisplayUsername={state.user?.username}
                                size="small"
                                displayEditTrick={true}
                                editTrick={editTrick}
                                editTrickCallback={async () => {
                                    await Promise.all([
                                        queryClient.invalidateQueries({ queryKey: ['userTrick', id, user_id] }), // whatever it fucking works
                                        queryClient.invalidateQueries({ queryKey: ['taggedUsers', trick?.trick_id] })
                                    ]);
                                }
                                }
                                deleteTrickCallback={() => {
                                    deleteTrickMutation.mutate()
                                }
                                }
                                onReportTrick={() => setReportOpen(true)}
                            />
                        </div>
                    </div>
                    {trick && videoProperties && (
                        <div className="xs:h-full xs:w-full sm:w-10/12 sm:h-10/12 md:w-6/12 md:h-6/12 l:w-5/12 l:h-5/12 xl:w-6/12 xl:h-6/12 mx-auto">
                            <HomePageVideoPlayer
                                isCapacitor={isNative}
                                src={trick.video}
                                thumbnail={trick.thumbnail}
                                id={trick.trick_id}
                                isCached={isCached}
                                isHorizontal={!videoProperties.is_vertical}
                                isExternal={videoProperties.is_external}
                                aspectRatio={videoProperties.aspect_ratio}
                                isAirtime={videoProperties.is_airtime}
                                isVimeo={videoProperties.is_vimeo}
                                isYoutube={videoProperties.is_youtube}
                                isScrolling={false}
                                scrollSpeed={0}
                                forceMuted={storyStudioOpen}
                            />
                        </div>
                    )}
                    <div className="flex flex-row font-bold text-2xl mx-3">
                        {trick?.trick_name}
                    </div>
                    <div className="flex flex-row ml-4 space-x-4 items-center cursor-pointer">
                        <img
                            onClick={() => onClickPerson(user?.username)}
                            className="shrink w-12 h-12 rounded-full"
                            src={user?.profile?.profile_pic || '/assets/photos/defaultProPic.png'}
                            alt="User Avatar"
                        />
                        <div className="flex flex-col space-y-0.5">
                            <UsernameDisplay
                                username={user?.username || user?.name}
                                className="font-bold text-xl"
                                onUsernameClickHandler={onClickPerson}
                                userId={trick?.user_id}
                                loggedInId={state?.user?.id}
                            />
                            {trick?.location && (
                                <div
                                    className="text-zinc-300 text-xs"
                                    onClick={() => handleTrickLocationClick(trick.location)}
                                >
                                    {trick.location}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="flex flex-row font-bold text-base overflow-x-auto mx-4">
                        <div className="flex flex-col">
                            {trick?.description && (
                                <div>{addTaggedUserToComments(trick.description)}</div>
                            )}
                            <div>{formattedDate}</div>
                        </div>
                    </div>
                    <div className="flex flex-row font-bold text-3xl mx-2 justify-between overflow-x-auto">
                        <div className="flex flex-row font-bold text-3xl items-center gap-x-2">
                            <TrickLike
                                trickId={trick?.trick_id}
                                isAuthed={isAuthed}
                                publicId={state?.user?.id}
                                userType={userType}
                                count={trickMetadataStats?.votes}
                            />
                            <TrickMapLocation location={trick?.location} handleTrickLocationClick={handleTrickLocationClick} />
                            <TrickDownload
                                isCapacitor={isNative}
                                trickId={trick?.trick_id}
                                trick={trick}
                                isSubscribed={isSubscribed}
                                subscriptionLevel={subscriptionLevel}
                            />
                            <BiskHome senderId={state?.user?.id} isAuthed={isAuthed} recipientId={trick?.user_id}
                                trickId={trick?.trick_id} trickName={trick?.trick_name}
                                recipientUsername={user?.username} />
                            {/* Owner-only Story Studio entry (Instagram + TikTok + Snap +
                                Save, stickers baked in). Non-owners use the share sheet below. */}
                            <ShareToStory
                                trick={trick}
                                isOwner={!!state?.user?.id && trick?.user_id === state?.user?.id}
                                variant="icon"
                                onOpenChange={setStoryStudioOpen}
                            />
                        </div>
                        <div className="flex flex-row font-bold text-3xl items-center gap-x-2">
                            <TrickShare isCapacitor={isNative} trickId={trick?.trick_id} trick={trick}
                                list={false}></TrickShare>
                            <TrickSaveComponent trickId={trick?.trick_id} isAuthed={isAuthed}
                                publicId={state?.user?.id} userType={userType} />
                        </div>
                    </div>
                    <VideoTagDisplay
                        taggedUsers={taggedUsers}
                        handleUserTagClick={handleUserTagClick}
                        trick={trick} tags={trick?.tags} handleTagClick={handleTagClick}
                        isAuthed={isAuthed} />
                    <div className="flex flex-row mx-5 gap-x-2 items-center">
                        <div>Comments</div>
                        <IDXChip bgColor="bg-transparent hover:bg-slate-500/50 click:bg-slate-500/50"
                            borderColor="border border-primary-secondary" text={trickCommentsCount} />
                    </div>
                    <CommentDisplay trickId={trick?.trick_id} publicId={state?.user?.id} isAuthed={isAuthed}
                        onClickPerson={onClickPerson} onSetTrickCommentCount={setTrickCommentsCount} />
                </div>
            </IonContent>
        </IonPage>
    );
};

export default TrickVideo